import type { FunnelSummary } from '@ops/contracts'

import { formatMetricValue, formatPeriodo } from '../../lib/format'
import { Panel, ProgressBar, StatusPill } from '../../ui'
import { Vacio } from '../comunes'
import { cuelloDelFunnel } from './narrativa'

/**
 * Funnel del período, una barra por etapa.
 *
 * El ancho de cada barra es su cantidad sobre la etapa de entrada, y al lado va
 * la conversión **con su base explícita**: `31,7% · 38 de 120`. La regla de
 * números derivables no admite un porcentaje cuya base no esté en la pantalla, y
 * un funnel es justamente donde más tentador es publicar el % solo.
 *
 * La etapa del cuello de botella lleva pill de crítico: texto además del tono.
 */
export interface FunnelPanelProps {
  funnel: FunnelSummary
  titulo?: string
  subtitulo?: string
}

export function FunnelPanel({
  funnel,
  titulo = 'De dónde a dónde se cae el funnel',
  subtitulo,
}: FunnelPanelProps) {
  const entrada = funnel.etapas[0]
  const cuello = cuelloDelFunnel(funnel)

  if (entrada === undefined) {
    return (
      <Panel titulo={titulo} padding="sm">
        <Vacio icono={null}
          mensaje="El funnel del período no tiene etapas cargadas"
          detalle="Las etapas se configuran en Adquisición y la carga es mensual por canal."
          punteada
        />
      </Panel>
    )
  }

  const base = Math.max(entrada.cantidad, 1)

  return (
    <Panel
      titulo={titulo}
      subtitulo={subtitulo ?? `Período ${formatPeriodo(funnel.periodo)}`}
      padding="sm"
      footer={
        cuello === null
          ? 'Ninguna etapa tiene conversión medida contra la anterior.'
          : `Cuello: de ${cuello.desde.toLowerCase()} a ${cuello.hasta.toLowerCase()} pasa el ${formatMetricValue(cuello.conversionPct, 'percent')}.`
      }
    >
      <ul className="flex list-none flex-col gap-2.5 p-0">
        {funnel.etapas.map((etapa, i) => {
          const previa = i === 0 ? null : (funnel.etapas[i - 1] ?? null)
          const esCuello =
            cuello !== null &&
            previa !== null &&
            cuello.desde === previa.nombre &&
            cuello.hasta === etapa.nombre

          return (
            <li key={etapa.nombre} data-testid="funnel-etapa" className="min-w-0">
              <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-meta font-medium text-text">
                  {etapa.nombre}{' '}
                  <b className="tabular font-semibold">
                    {formatMetricValue(etapa.cantidad, 'count')}
                  </b>
                </span>

                {etapa.conversionPct === null || previa === null ? (
                  <span className="text-micro text-muted-strong">
                    etapa de entrada, sin conversión previa
                  </span>
                ) : (
                  <span className="flex flex-wrap items-center gap-1.5">
                    {esCuello ? (
                      <StatusPill severidad="critical" caps size="sm">
                        Cuello
                      </StatusPill>
                    ) : null}
                    {/* Porcentaje con su base al lado: siempre recalculable. */}
                    <span className="tabular text-micro text-muted-strong">
                      {formatMetricValue(etapa.conversionPct, 'percent')} ·{' '}
                      {formatMetricValue(etapa.cantidad, 'count')} de{' '}
                      {formatMetricValue(previa.cantidad, 'count')}{' '}
                      {previa.nombre.toLowerCase()}
                    </span>
                  </span>
                )}
              </div>

              <ProgressBar
                pct={(etapa.cantidad / base) * 100}
                label={`${etapa.nombre}: ${etapa.cantidad} de ${entrada.cantidad} ${entrada.nombre.toLowerCase()}`}
                severidad={esCuello ? 'critical' : i === 0 ? 'unknown' : 'ok'}
                etiqueta={null}
                size="md"
              />
            </li>
          )
        })}
      </ul>
    </Panel>
  )
}
